import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import apiClient from '../services/apiClient';

const AgentChatContext = createContext(null);

const SESSION_ID = `chat_${Date.now()}`;

// Get the base URL for streaming requests (bypasses axios)
function getBaseUrl() {
  // apiClient.defaults.baseURL is set by the app
  return apiClient.defaults.baseURL || '';
}

// Get auth token from localStorage (matches existing auth pattern)
function getAuthToken() {
  return localStorage.getItem('token');
}

// Get API key (matches apiClient interceptor pattern)
function getApiKey() {
  return process.env.REACT_APP_API_KEY || (process.env.NODE_ENV !== 'production' ? 'local-development-api-key' : '');
}

export function AgentChatProvider({ children }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [streaming, setStreaming] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeAgent, setActiveAgent] = useState(null);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [activeSkill, setActiveSkill] = useState(null); // { skill, totalSteps, steps: [{name, label, status}] }
  const [activityPhase, setActivityPhase] = useState(null); // 'thinking' | 'routing' | 'calling_tools' | 'streaming'
  const [activeTools, setActiveTools] = useState([]); // [{ name, startTime }]
  const [completedTools, setCompletedTools] = useState([]); // [{ name, durationMs }]
  const [requestStartTime, setRequestStartTime] = useState(null);
  const abortRef = useRef(null);

  // Cmd+J / Ctrl+J to toggle sidebar
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'j') {
        e.preventDefault();
        setSidebarOpen(prev => !prev);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // ---------------------------------------------------------------------------
  // Streaming send (SSE) — used by sidebar
  // ---------------------------------------------------------------------------
  const sendMessage = useCallback(async (text) => {
    if (!text.trim() || loading) return null;

    const userMsg = { role: 'user', content: text.trim(), timestamp: Date.now() };
    setMessages(prev => [...prev, userMsg]);
    setLoading(true);
    setStreaming(true);
    setActiveAgent(null);
    setActivityPhase('thinking'); // Show activity indicator immediately, don't wait for SSE
    setActiveTools([]);
    setCompletedTools([]);
    setRequestStartTime(Date.now());

    // Add placeholder assistant message for streaming
    const assistantMsgId = Date.now();
    const placeholderMsg = {
      id: assistantMsgId,
      role: 'assistant',
      content: '',
      toolCalls: [],
      activeAgent: null,
      streaming: true,
      timestamp: assistantMsgId
    };
    setMessages(prev => [...prev, placeholderMsg]);

    try {
      const abortController = new AbortController();
      abortRef.current = abortController;

      const response = await fetch(`${getBaseUrl()}/admin/agent-chat/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getAuthToken()}`,
          'x-api-key': getApiKey()
        },
        body: JSON.stringify({ message: text.trim(), sessionId: SESSION_ID }),
        signal: abortController.signal
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const jsonStr = line.slice(6);
          let event;
          try { event = JSON.parse(jsonStr); } catch { continue; }

          switch (event.type) {
            case 'thinking':
              setActivityPhase('thinking');
              break;

            case 'routing':
              setActivityPhase('routing');
              if (event.agent) setActiveAgent(event.agent);
              break;

            case 'token':
              setActivityPhase('streaming');
              setMessages(prev => prev.map(m =>
                m.id === assistantMsgId
                  ? { ...m, content: m.content + event.text }
                  : m
              ));
              break;

            case 'tool_start':
              setActivityPhase('calling_tools');
              setActiveTools(prev => [...prev, { name: event.name, startTime: Date.now() }]);
              setMessages(prev => prev.map(m =>
                m.id === assistantMsgId
                  ? { ...m, toolCalls: [...(m.toolCalls || []), event.name] }
                  : m
              ));
              break;

            case 'tool_done':
              setActiveTools(prev => prev.filter(t => t.name !== event.name));
              setCompletedTools(prev => [...prev, { name: event.name, durationMs: event.durationMs, error: event.error }]);
              break;

            // Backward compat for old events
            case 'tool_call':
              setActivityPhase('calling_tools');
              setMessages(prev => prev.map(m =>
                m.id === assistantMsgId
                  ? { ...m, toolCalls: [...(m.toolCalls || []), event.name] }
                  : m
              ));
              break;

            case 'tool_result':
              break;

            case 'agent_switch':
              setActiveAgent(event.agent);
              setMessages(prev => prev.map(m =>
                m.id === assistantMsgId
                  ? { ...m, activeAgent: event.agent }
                  : m
              ));
              break;

            case 'approval_needed':
              setPendingApprovals(prev => [...prev, {
                approvalId: event.approvalId,
                tool: event.tool,
                args: event.args,
                description: event.description
              }]);
              break;

            case 'skill_start':
              setActiveSkill({
                skill: event.skill,
                totalSteps: event.totalSteps,
                description: event.description,
                steps: [],
              });
              break;

            case 'skill_step_progress':
              setActiveSkill(prev => {
                if (!prev) return prev;
                const steps = [...prev.steps];
                const existing = steps.findIndex(s => s.name === event.step);
                const stepData = { name: event.step, label: event.label, status: event.status, error: event.error };
                if (existing >= 0) {
                  steps[existing] = stepData;
                } else {
                  steps.push(stepData);
                }
                return { ...prev, steps };
              });
              break;

            case 'skill_complete':
              setActiveSkill(prev => prev ? { ...prev, completed: true, success: event.success } : prev);
              // Clear after a short delay so user can see the final state
              setTimeout(() => setActiveSkill(null), 3000);
              break;

            case 'error':
              setMessages(prev => prev.map(m =>
                m.id === assistantMsgId
                  ? { ...m, content: m.content + (event.message || 'An error occurred'), isError: true, streaming: false }
                  : m
              ));
              break;

            case 'done':
              setActivityPhase(null);
              setActiveTools([]);
              setMessages(prev => prev.map(m =>
                m.id === assistantMsgId
                  ? { ...m, streaming: false, activeAgent: event.activeAgent }
                  : m
              ));
              break;
          }
        }
      }

      // Mark streaming complete
      setMessages(prev => prev.map(m =>
        m.id === assistantMsgId ? { ...m, streaming: false } : m
      ));
    } catch (err) {
      if (err.name === 'AbortError') return null;

      const errMsg = err.message || 'Failed to get response';
      setMessages(prev => prev.map(m =>
        m.id === assistantMsgId
          ? { ...m, content: `Error: ${errMsg}`, isError: true, streaming: false }
          : m
      ));
    } finally {
      setLoading(false);
      setStreaming(false);
      setActivityPhase(null);
      setActiveTools([]);
      setRequestStartTime(null);
      abortRef.current = null;
    }

    return null;
  }, [loading]);

  // ---------------------------------------------------------------------------
  // Non-streaming send — used by command palette
  // ---------------------------------------------------------------------------
  const sendMessageSync = useCallback(async (text) => {
    if (!text.trim() || loading) return null;

    const userMsg = { role: 'user', content: text.trim(), timestamp: Date.now() };
    setMessages(prev => [...prev, userMsg]);
    setLoading(true);

    try {
      const response = await apiClient.post('/admin/agent-chat', {
        message: text.trim(),
        sessionId: SESSION_ID
      });

      const assistantMsg = {
        role: 'assistant',
        content: response.data.content,
        toolCalls: response.data.toolCalls || [],
        activeAgent: response.data.activeAgent || null,
        timestamp: Date.now()
      };

      setMessages(prev => [...prev, assistantMsg]);
      if (response.data.activeAgent) setActiveAgent(response.data.activeAgent);
      return assistantMsg;
    } catch (err) {
      const errMsg = err.response?.data?.error || err.message || 'Failed to get response';
      const errorAssistantMsg = {
        role: 'assistant',
        content: `Error: ${errMsg}`,
        isError: true,
        timestamp: Date.now()
      };
      setMessages(prev => [...prev, errorAssistantMsg]);
      return errorAssistantMsg;
    } finally {
      setLoading(false);
    }
  }, [loading]);

  // ---------------------------------------------------------------------------
  // Approval actions
  // ---------------------------------------------------------------------------
  const approveAction = useCallback(async (approvalId) => {
    try {
      await apiClient.post('/admin/agent-chat/approve', { approvalId });
      setPendingApprovals(prev => prev.filter(a => a.approvalId !== approvalId));
    } catch (err) {
      console.error('Failed to approve action:', err);
    }
  }, []);

  const rejectAction = useCallback(async (approvalId) => {
    try {
      await apiClient.post('/admin/agent-chat/reject', { approvalId });
      setPendingApprovals(prev => prev.filter(a => a.approvalId !== approvalId));
    } catch (err) {
      console.error('Failed to reject action:', err);
    }
  }, []);

  // ---------------------------------------------------------------------------
  // Clear / sidebar controls
  // ---------------------------------------------------------------------------
  const clearChat = useCallback(async () => {
    if (abortRef.current) abortRef.current.abort();
    setMessages([]);
    setActiveAgent(null);
    setPendingApprovals([]);
    setActiveSkill(null);
    setActivityPhase(null);
    setActiveTools([]);
    setCompletedTools([]);
    setRequestStartTime(null);
    try {
      await apiClient.delete(`/admin/agent-chat?sessionId=${SESSION_ID}`);
    } catch (e) {
      // silent
    }
  }, []);

  const stopStreaming = useCallback(() => {
    if (abortRef.current) abortRef.current.abort();
  }, []);

  const openSidebar = useCallback(() => setSidebarOpen(true), []);
  const closeSidebar = useCallback(() => setSidebarOpen(false), []);
  const toggleSidebar = useCallback(() => setSidebarOpen(prev => !prev), []);

  return (
    <AgentChatContext.Provider value={{
      messages,
      loading,
      streaming,
      sidebarOpen,
      activeAgent,
      pendingApprovals,
      activeSkill,
      activityPhase,
      activeTools,
      completedTools,
      requestStartTime,
      sendMessage,
      sendMessageSync,
      approveAction,
      rejectAction,
      clearChat,
      stopStreaming,
      openSidebar,
      closeSidebar,
      toggleSidebar
    }}>
      {children}
    </AgentChatContext.Provider>
  );
}

export function useAgentChat() {
  const ctx = useContext(AgentChatContext);
  if (!ctx) throw new Error('useAgentChat must be used within AgentChatProvider');
  return ctx;
}
